import {
  FacilityFlowCoreModulesFleetEntitiesVehicleCheckKind as VehicleCheckKindEnum,
  type FacilityFlowCoreModulesFleetDtosVehicleCheckDto as VehicleCheckDto,
  type FacilityFlowCoreModulesFleetEntitiesVehicleCheckKind as VehicleCheckKind,
} from '@/api/generated/models';
import { formatDate, parseIsoDate } from '@/utils/dateUtils';

/** Display order of the checks: first aid kit, inspection („Pickerl“), vignette (FAFD-69). */
export const VEHICLE_CHECK_KINDS: VehicleCheckKind[] = [
  VehicleCheckKindEnum.FirstAidKit,
  VehicleCheckKindEnum.Inspection,
  VehicleCheckKindEnum.Vignette,
];

/** A check expiring within this many days is shown as due soon. */
export const DUE_SOON_DAYS = 30;

export type VehicleCheckStatus = 'ok' | 'dueSoon' | 'expired' | 'missing';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Status of a check on `today` (both `YYYY-MM-DD`): valid through its last day, due soon in the
 * last {@link DUE_SOON_DAYS} days, expired afterwards; no check at all is `missing`.
 */
export function vehicleCheckStatus(validUntil: string | null | undefined, today: string): VehicleCheckStatus {
  if (!validUntil) return 'missing';
  const daysLeft = Math.round((parseIsoDate(validUntil).getTime() - parseIsoDate(today).getTime()) / MS_PER_DAY);
  if (daysLeft < 0) return 'expired';
  return daysLeft <= DUE_SOON_DAYS ? 'dueSoon' : 'ok';
}

/** The check of a kind among the vehicle's current checks, if one was recorded. */
export function currentCheckOf(
  checks: VehicleCheckDto[] | null | undefined,
  kind: VehicleCheckKind,
): VehicleCheckDto | undefined {
  return checks?.find((c) => c.kind === kind);
}

/** The valid-until day of a check; an inspection only by month („02/2027“), as on the sticker. */
export function formatValidUntil(kind: VehicleCheckKind | undefined, validUntil: string | undefined): string {
  if (!validUntil) return '–';
  const date = parseIsoDate(validUntil);
  return kind === VehicleCheckKindEnum.Inspection
    ? formatDate(date, { month: '2-digit', year: 'numeric' })
    : formatDate(date);
}

/** An inspection is due in a month: the `YYYY-MM` of a month input becomes its first day. */
export function validUntilFromInput(kind: VehicleCheckKind, value: string): string {
  return kind === VehicleCheckKindEnum.Inspection ? `${value}-01` : value;
}
